import React, { useEffect, useState } from "react";
import { Box, Text } from "@chakra-ui/react";
import { useLocation, useHistory } from "react-router-dom/";
import { useLazyQuery } from "@apollo/client";
import { format } from "date-fns";
import { QSETS } from "../../graphql/queries";
import { PaginatedDataTable } from "../../components/PaginatedDataTable";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { SectionCard } from "../../components/SectionCard";
import { QSetActions, EmptyCell } from "../../components/QSetActions";
import { PracticeIcon } from "../../components/Icons/PracticeIcon";
import {
  getAvgTimeTakenPerQuestion,
  secondsToMinutesAndSeconds,
} from "../../utils/helper";

export const PreviousTestsPage = ({ user, userRole, loadingCallback }) => {
  const [qsets, setQsets] = useState([]);
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const appid = queryParams.get("appid");
  const userid = queryParams.get("userid");
  const previewModeParam = (queryParams.get("preview_mode") || "").toLowerCase();
  const isPreviewMode = ["1", "true", "yes"].includes(previewModeParam);
  const directModeParam = (queryParams.get("direct_mode") || queryParams.get("direct") || "").toLowerCase();
  const isDirectMode = ["1", "true", "yes"].includes(directModeParam);
  const canPractice = userRole === "Student" || isPreviewMode || isDirectMode;
  const effectiveUserId = (isPreviewMode || isDirectMode) ? (userid || user.id) : user.id;
  const history = useHistory();
  const [hasLoaded, setHasLoaded] = useState(false);
  const modeParams = `${isPreviewMode ? "&preview_mode=1" : ""}${isDirectMode ? "&direct_mode=1" : ""}`;

  const [getQsets] = useLazyQuery(QSETS, {
    onCompleted: (data) => {
      setQsets(
        data?.qsets.map((qset) => {
          const submission =
            qset?.qSetSubmissions.length > 0 ? qset?.qSetSubmissions[0] : {};
          const totalQuestions = submission.id
            ? submission?.qset?.dynosUnderQSet.length
            : null;
          return {
            ...qset,
            name: qset?.qSetName,
            submittedAt: submission?.id && submission?.isAllQuestionSubmitted
              ? format(new Date(submission?.updatedAt), "MM/dd/yyyy")
              : null,
            score: submission?.id && submission?.isAllQuestionSubmitted
              ? `${submission?.qsetScore}/${totalQuestions}`
              : null,
            totalTimeTaken: submission?.id && submission?.isAllQuestionSubmitted
              ? secondsToMinutesAndSeconds(submission?.qsetTimeTaken)
              : null,
            avgTimeTaken: submission?.id && submission?.isAllQuestionSubmitted
              ? getAvgTimeTakenPerQuestion(
                  Number(submission?.qsetTimeTaken),
                  Number(totalQuestions),
                )
              : null,
            status: submission?.id
              ? submission?.isAllQuestionSubmitted
                ? "View Responses"
                : "Continue"
              : "Try Now",
          };
        }),
      );
      setHasLoaded(true);
      loadingCallback(false, "");
    },
    onError: () => {
      setHasLoaded(true);
      loadingCallback(false, "");
    },
    fetchPolicy: "network-only"
  });

  useEffect(() => {
    loadingCallback(true, "loading previous tests...");
    getQsets({
      variables: {
        where: {
          AND: [
            {
              QApp: {
                id: { equals: appid },
              },
            },
              {
                userId: {
                  equals: effectiveUserId,
                },
              },
            ],
        },
      },
    });
  }, [appid, effectiveUserId, getQsets, loadingCallback]);

  const practiceQset = (value) => {
    sessionStorage.setItem(
      "fromPath",
      `/previoustests?appid=${appid}&userid=${userid}${isPreviewMode ? "&preview_mode=1" : ""}${isDirectMode ? "&direct_mode=1" : ""}`
    )
    history.push(
      value.status === "View Responses"
        ? `/qreview/?appid=${appid}&userid=${userid}&qsetid=${value.id}${isPreviewMode ? "&preview_mode=1" : ""}${isDirectMode ? "&direct_mode=1" : ""}`
        : `/qviewer/?appid=${appid}&userid=${userid}&qsetid=${value.id}${isPreviewMode ? "&preview_mode=1" : ""}${isDirectMode ? "&direct_mode=1" : ""}`
    );
  };

  const qsetColumns = [
    {
      Header: "Name",
      accessor: "name",
      Cell: ({ cell: { value } }) => <span className="qset-table__name">{value}</span>,
    },
    {
      Header: "Date Of Test",
      accessor: "submittedAt",
      Cell: ({ cell: { value } }) => value || <EmptyCell />,
    },
    {
      Header: "Score",
      accessor: "score",
      Cell: ({ cell: { value } }) => (value ? <span className="qset-score">{value}</span> : <EmptyCell />),
    },
    {
      Header: "Total Time Taken",
      accessor: "totalTimeTaken",
      Cell: ({ cell: { value } }) => value || <EmptyCell />,
    },
    {
      Header: "Avg Time Per Question",
      accessor: "avgTimeTaken",
      Cell: ({ cell: { value } }) => value || <EmptyCell />,
    },
    ...(canPractice
      ? [
          {
            id: "actions",
            Header: () => <span className="sr-only">Actions</span>,
            disableSortBy: true,
            Cell: ({ cell }) => (
              <QSetActions
                status={cell.row.original.status}
                onOpen={() => practiceQset(cell.row.original)}
                onTryAgain={() => history.push(`/qviewer/?appid=${appid}&userid=${userid}&qsetid=${cell.row.original.id}${modeParams}`)}
              />
            ),
          },
        ]
      : []),
  ];

  const finishedCount = qsets.filter((qset) => qset.status === "View Responses").length;

  return (
    <Box padding={{ base: "20px 10px", md: "20px 20px" }}>
      <Box className="page-sections">
        <SectionCard
          headingAs="h1"
          title="Previous Tests"
          subtitle={
            qsets.length
              ? `${qsets.length} question ${qsets.length === 1 ? "set" : "sets"}, ${finishedCount} finished. Review your answers or try a set again.`
              : "Study from your earlier question sets."
          }
          icon={<PracticeIcon />}
          action={{
            label: "Home",
            icon: <ArrowBackIcon />,
            onClick: () => history.push(`/?appid=${appid}&userid=${userid}${modeParams}`),
          }}
        >
          {hasLoaded && qsets.length === 0 ? (
            <Box className="section-empty">
              <Text className="section-empty__title">No practice tests yet</Text>
              <Text className="section-empty__text">Start learning or generate a question set from the Home page.</Text>
            </Box>
          ) : (
            <PaginatedDataTable
              columns={qsetColumns}
              data={qsets}
              showNumberOfRecords={100}
            />
          )}
        </SectionCard>
      </Box>
    </Box>
  );
};
